import { Button, Col, ConfigProvider, Form, Row, Switch } from "antd";
import {
  CloseOutlined,
  CreditCardOutlined,
  LoadingOutlined,
  LockOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  ApplePay,
  CreditCard,
  GooglePay,
  PaymentForm,
} from "react-square-web-payments-sdk";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import usePost from "../hooks/usePost";
import useGet from "../hooks/useGet";
import { openErrorNotification } from "../utils/toast";
import CustomInput from "./inputWidgets/CustomInput";
import CustomSelect from "./inputWidgets/CustomSelect";
import { offlineMethodOptions } from "../../utils/Common";
import {
  brandKeyFromCard,
  CardBrandIcon,
  formatExpiresOn,
  CARD_EXPIRATION_ERROR_MESSAGE,
  isCardExpirationAcceptable,
  MAX_SAVED_CARDS,
  normalizeCardsResponse,
} from "../utils/cardPaymentUtils";
import PaymentSuccessModal, {
  computeValidityDates,
} from "./PaymentSuccessModal";
import gpayLogo from "../../assets/images/GPay.webp";
import applePayLogo from "../../assets/images/ApplyPay.webp";

/**
 * Mount success modal outside PaymentForm so it survives when the parent
 * "Make Payment" Modal closes (callers use destroyOnClose).
 */
function openDetachedPaymentSuccessModal({
  dealerEmail,
  paymentNo,
  amount,
  method,
  paidAt,
  validTill,
  renewalAt,
  onClose,
}) {
  const container = document.createElement("div");
  container.setAttribute("data-payment-success-root", "");
  document.body.appendChild(container);
  const root = createRoot(container);

  const handleClose = () => {
    try {
      onClose?.();
    } finally {
      root.unmount();
      container.remove();
    }
  };

  const colorPrimary =
    getComputedStyle(document.documentElement)
      .getPropertyValue("--text-color")
      .trim() || undefined;

  root.render(
    <ConfigProvider
      theme={{
        components: {
          Button: {
            colorPrimary,
            colorPrimaryHover: "white",
          },
        },
        token: { fontFamily: '"Poppins", sans-serif' },
      }}
    >
      <PaymentSuccessModal
        open
        onClose={handleClose}
        dealerEmail={dealerEmail}
        paymentNo={paymentNo}
        amount={amount}
        method={method}
        paidAt={paidAt}
        validTill={validTill}
        renewalAt={renewalAt}
      />
    </ConfigProvider>,
  );
}

const SQUARE_PAY_BUTTON_SELECTOR =
  "#make-payment-square-root #rswp-card-button, #make-payment-square-root button";

function getApiBase() {
  const raw = process.env.REACT_APP_API_URL;
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s || s === "undefined") return "";
  return s.replace(/\/+$/, "");
}

const apiBase = getApiBase();

function apiUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  return apiBase ? `${apiBase}${p}` : p;
}

const GooglePayLogo = () => (
  <img
    src={gpayLogo}
    alt="GPay"
    className="h-3.5 w-auto max-w-[56px] object-contain pointer-events-none select-none"
    draggable={false}
  />
);

const ApplePayLogo = () => (
  <img
    src={applePayLogo}
    alt="Apple Pay"
    className="h-3.5 w-auto max-w-[56px] object-contain pointer-events-none select-none"
    draggable={false}
  />
);

function canUseApplePay() {
  try {
    return (
      typeof window !== "undefined" &&
      !!window.ApplePaySession &&
      typeof window.ApplePaySession.canMakePayments === "function" &&
      window.ApplePaySession.canMakePayments()
    );
  } catch {
    return false;
  }
}

function formatCardBrandLabel(brand) {
  const raw = String(brand ?? "")
    .trim()
    .replace(/_/g, " ");
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (lower.includes("visa")) return "Visa";
  if (lower.includes("master")) return "Mastercard";
  if (lower.includes("amex") || lower.includes("american express"))
    return "Amex";
  if (lower.includes("discover")) return "Discover";
  if (lower.includes("diner")) return "Diners";
  if (lower.includes("jcb")) return "JCB";
  if (lower.includes("union")) return "UnionPay";
  return raw
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function formatCardMethodLabel(brand, last4) {
  const b = formatCardBrandLabel(brand);
  const l4 = last4 != null && last4 !== "" ? String(last4) : "";
  if (b && l4) return `${b} **** ${l4}`;
  if (l4) return `**** ${l4}`;
  if (b) return b;
  return "";
}

function methodLabelFromTokenResult(tokenResult) {
  const method = String(tokenResult?.details?.method ?? "").toLowerCase();
  if (method.includes("apple")) return "Apple Pay";
  if (method.includes("google")) return "Google Pay";
  const card = tokenResult?.details?.card;
  if (card) {
    return (
      formatCardMethodLabel(card.brand ?? card.cardBrand, card.last4) || "Card"
    );
  }
  return "Card";
}

function extractPaymentPayload(postData) {
  if (!postData || typeof postData !== "object") return {};
  return postData.data?.data ?? postData.data ?? postData;
}

function resolvePaidAtDate(payment) {
  const raw =
    payment?.PaymentTime ??
    payment?.payment_time ??
    payment?.PaidAt ??
    payment?.paid_at ??
    null;
  if (raw == null || raw === "") return new Date();
  if (typeof raw === "number" || (/^\d+$/.test(String(raw)) && String(raw).length <= 12)) {
    const n = Number(raw);
    // Unix seconds vs ms
    return new Date(n < 1e12 ? n * 1000 : n);
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function resolvePaymentNo(payment, postData) {
  return (
    payment?.PaymentNo ??
    payment?.payment_no ??
    payment?.id ??
    postData?.PaymentNo ??
    postData?.data?.PaymentNo ??
    null
  );
}

const MyPaymentForm = ({
  amount,
  setPaymentModal,
  InvoiceNo,
  OrderName,
  refetch,
  showCardLayout,
  setShowCardLayout,
  onPaymentSuccess,
  beforePayment,
  DealerNo,
  /** When true (site/device), show Valid till / Renewal per billing rules */
  showValidityDates = false,
  /** 0 = Invoice, 1 = Transaction */
  billingType = null,
  /** 0 = Monthly, 1 = Lifetime, 2 = Yearly (if used) */
  licensePeriod = null,
  /** Optional override; otherwise profile / session email */
  dealerEmail: dealerEmailProp,
}) => {
  const [loading, setLoading] = useState(false);
  /** saved | new */
  const [cardTab, setCardTab] = useState("saved");
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [saveCardForFuture, setSaveCardForFuture] = useState(true);
  const [internalShowCardLayout, internalSetShowCardLayout] = useState(true);
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [manualForm] = Form.useForm();
  const pendingMethodRef = useRef("Card");

  useEffect(() => {
    setApplePayAvailable(canUseApplePay());
  }, []);

  const isCardLayoutControlled =
    typeof showCardLayout === "boolean" &&
    typeof setShowCardLayout === "function";
  const showCardComingSoonMessage =
    String(process.env.REACT_APP_CARD_PAYMENT_COMING_SOON ?? "true")
      .trim()
      .toLowerCase() !== "false";

  const effectiveShowCardLayout = isCardLayoutControlled
    ? showCardLayout
    : internalShowCardLayout;
  const effectiveSetShowCardLayout = isCardLayoutControlled
    ? setShowCardLayout
    : internalSetShowCardLayout;

  const getNumericAmount = (value) => {
    const raw = value ?? "";
    const num = Number(String(raw).replace(/[^0-9.-]+/g, ""));
    return Number.isFinite(num) ? num : NaN;
  };

  const numericAmount = getNumericAmount(amount);
  const displayAmountWithCurrency = Number.isFinite(numericAmount)
    ? `$${numericAmount.toFixed(2)}`
    : amount;
  const amountForWallets = Number.isFinite(numericAmount)
    ? numericAmount.toFixed(2)
    : "0.00";

  const dealerNoStr =
    DealerNo != null && DealerNo !== "" ? String(DealerNo) : "";
  const cardsUrl = useMemo(() => {
    if (!dealerNoStr || !effectiveShowCardLayout || showCardComingSoonMessage) {
      return null;
    }
    return apiUrl(`/api/dealers/${dealerNoStr}/cards/`);
  }, [dealerNoStr, effectiveShowCardLayout, showCardComingSoonMessage]);

  const {
    data: cardsData,
    loading: cardsLoading,
    error: cardsError,
  } = useGet(cardsUrl);

  const profileUrl = useMemo(() => {
    if (dealerEmailProp) return null;
    return apiUrl("/api/accounts/profile/");
  }, [dealerEmailProp]);

  const { data: profileData } = useGet(profileUrl);

  const resolvedDealerEmail = useMemo(() => {
    if (dealerEmailProp) return String(dealerEmailProp);
    try {
      const ud = JSON.parse(sessionStorage.getItem("userData") || "null");
      const fromSession = ud?.email || ud?.Email || "";
      if (fromSession) return String(fromSession);
    } catch {
      /* ignore */
    }
    const fromProfile =
      profileData?.data?.Email ??
      profileData?.Email ??
      profileData?.data?.email ??
      "";
    return fromProfile ? String(fromProfile) : "";
  }, [dealerEmailProp, profileData]);

  const cards = useMemo(() => normalizeCardsResponse(cardsData), [cardsData]);

  const hasSavedCards = !cardsLoading && cards.length > 0;
  const showCardTabs = hasSavedCards;
  const activeCardTab = showCardTabs ? cardTab : "new";
  const canSaveNewCard = cards.length < MAX_SAVED_CARDS;

  useEffect(() => {
    if (!canSaveNewCard) {
      setSaveCardForFuture(false);
    }
  }, [canSaveNewCard]);

  useEffect(() => {
    if (cardsLoading) return;
    if (!cards.length) {
      setSelectedCardId(null);
      setCardTab("new");
      return;
    }
    setCardTab((prev) => (prev === "new" ? prev : "saved"));
    setSelectedCardId((prev) => {
      if (
        prev != null &&
        cards.some((c) => String(c.DealerCardNo) === String(prev))
      ) {
        return prev;
      }
      const def =
        cards.find((c) => c.IsDefault) ||
        cards.find((c) => c.SquareCardID) ||
        cards[0];
      return def?.DealerCardNo ?? null;
    });
  }, [cards, cardsLoading]);

  const selectedCard = useMemo(
    () => cards.find((c) => String(c.DealerCardNo) === String(selectedCardId)),
    [cards, selectedCardId],
  );

  const postUrl = `${process.env.REACT_APP_API_URL}/api/payments/`;
  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);

  useEffect(() => {
    refetch?.();
  }, [postData, postLoading]);

  const submitPayment = async (payload) => {
    const okToPay = beforePayment ? await beforePayment() : true;
    if (!okToPay) {
      setLoading(false);
      return;
    }
    postAction(payload);
  };

  const handleTokenReceived = async (tokenResult) => {
    try {
      setLoading(true);
      if (tokenResult?.status !== "OK" || !tokenResult?.token) {
        const err0 = tokenResult?.errors?.[0];
        openErrorNotification(
          err0?.message || "Could not process payment method.",
        );
        setLoading(false);
        return;
      }
      const cardDetails = tokenResult?.details?.card;
      if (
        cardDetails?.expMonth != null &&
        cardDetails?.expYear != null &&
        !isCardExpirationAcceptable(cardDetails.expMonth, cardDetails.expYear)
      ) {
        openErrorNotification(CARD_EXPIRATION_ERROR_MESSAGE);
        setLoading(false);
        return;
      }
      pendingMethodRef.current = methodLabelFromTokenResult(tokenResult);
      const safeAmount = getNumericAmount(amount);
      await submitPayment({
        dealer_id: DealerNo,
        source_id: tokenResult.token,
        amount: safeAmount,
        save_card: canSaveNewCard && saveCardForFuture,
      });
    } catch (e) {
      setLoading(false);
    }
  };

  const handlePaySavedCard = async () => {
    try {
      if (!selectedCard?.SquareCardID) {
        openErrorNotification("Select a saved card to pay.");
        return;
      }
      setLoading(true);
      pendingMethodRef.current =
        formatCardMethodLabel(selectedCard.CardBrand, selectedCard.CardLast4) ||
        "Saved card";
      const safeAmount = getNumericAmount(amount);
      await submitPayment({
        dealer_id: DealerNo,
        amount: safeAmount,
        dealer_card_no: selectedCard.SquareCardID,
      });
    } catch (e) {
      setLoading(false);
    }
  };

  const handlePayWithoutNewCard = async () => {
    try {
      const values = await manualForm.validateFields();
      const offlineMethod = Number(values.offline_method);
      const offlineRef =
        values.offline_method_reference != null
          ? String(values.offline_method_reference).trim()
          : "";
      const methodLabel =
        offlineMethodOptions.find((o) => o.value === offlineMethod)?.label ||
        "Manual";
      const safeAmount = getNumericAmount(amount);
      setLoading(true);
      pendingMethodRef.current = methodLabel;
      await submitPayment({
        dealer_id: DealerNo,
        amount: safeAmount,
        payment_mode: "MANUAL",
        offline_method: offlineMethod,
        ...(offlineRef ? { offline_method_reference: offlineRef } : {}),
      });
    } catch (e) {
      setLoading(false);
    }
  };

  const handlePayClick = () => {
    if (!effectiveShowCardLayout) {
      handlePayWithoutNewCard();
      return;
    }
    if (showCardComingSoonMessage) return;

    if (activeCardTab === "saved") {
      handlePaySavedCard();
      return;
    }

    setLoading(true);
    requestAnimationFrame(() => {
      const btn = document.querySelector(SQUARE_PAY_BUTTON_SELECTOR);
      if (btn) {
        btn.click();
      } else {
        setLoading(false);
        openErrorNotification(
          "Card form is not ready. Wait a moment and try again.",
        );
      }
    });
  };

  useEffect(() => {
    if (postError) {
      setLoading(false);
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      let cancelled = false;
      (async () => {
        try {
          const afterClose = await onPaymentSuccess?.(postData);
          if (cancelled) return;

          const payment = extractPaymentPayload(postData);
          const paidAt = resolvePaidAtDate(payment);
          const paymentNo = resolvePaymentNo(payment, postData);
          const responseLast4 = payment?.CardLast4 ?? payment?.card_last4;
          let method = pendingMethodRef.current || "Card";
          if (
            (!method || method === "Card" || method === "Saved card") &&
            responseLast4
          ) {
            method = formatCardMethodLabel(null, responseLast4) || method;
          }
          const amountValue =
            payment?.Amount ?? payment?.amount ?? numericAmount ?? amount;

          const { validTill, renewalAt } = computeValidityDates({
            showValidityDates,
            billingType,
            licensePeriod,
            paidAt,
          });

          // Detach success modal first so it survives parent destroyOnClose,
          // then close Make Payment UI immediately (avoids empty modal chrome).
          openDetachedPaymentSuccessModal({
            dealerEmail: resolvedDealerEmail,
            paymentNo,
            amount: amountValue,
            method,
            paidAt,
            validTill,
            renewalAt,
            onClose: () => {
              if (typeof afterClose === "function") {
                try {
                  afterClose();
                } catch {
                  /* ignore navigation errors */
                }
              }
            },
          });
          setPaymentModal?.(false);
        } catch (e) {
          if (cancelled) return;
          openErrorNotification(
            e?.response?.data?.message || e?.message || "Something went wrong",
          );
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
  }, [postError, postData, postLoading]);

  const paymentHelperText = effectiveShowCardLayout
    ? `Enter your card details to pay ${displayAmountWithCurrency}.`
    : `Complete payment of ${displayAmountWithCurrency} manually.`;
  const cardComingSoonMessage =
    "We are preparing a card payment system.\nPlease contact 857-346-0130 for Prepurchased License.";

  const applicationId = process.env.REACT_APP_APPLICATION_ID;
  const locationId = process.env.REACT_APP_LOCATION_ID;

  const createPaymentRequest = () => ({
    countryCode: "US",
    currencyCode: "USD",
    total: {
      amount: amountForWallets,
      label: "Total",
    },
  });

  return (
    <>
      <style>{`
        #make-payment-square-root #rswp-card-button { display: none !important; }
        #make-payment-wallets .sq-wallet-hit {
          position: absolute;
          inset: 0;
          opacity: 0.001;
          z-index: 2;
          overflow: hidden;
        }
        #make-payment-wallets .sq-wallet-hit > *,
        #make-payment-wallets .sq-wallet-hit iframe,
        #make-payment-wallets .sq-wallet-hit button,
        #make-payment-wallets .sq-wallet-hit div {
          width: 100% !important;
          height: 100% !important;
          min-height: 100% !important;
        }
        .manual-payment-fields .ant-form-item {
          margin-bottom: 0 !important;
        }
      `}</style>

      {!effectiveShowCardLayout && (
        <div className="px-2 mb-3">
          <p className="text-sm font-[Poppins] mb-3">{paymentHelperText}</p>
          <Form
            form={manualForm}
            layout="vertical"
            autoComplete="off"
            className="manual-payment-fields"
            initialValues={{ offline_method: undefined }}
          >
            <Row gutter={[16, 8]}>
              <Col span={24}>
                <CustomSelect
                  label="Offline Method"
                  name="offline_method"
                  options={offlineMethodOptions}
                  placeholder="Please select"
                  required
                  allowClear={false}
                />
              </Col>
              <Col span={24}>
                <CustomInput
                  label="Offline Method Reference"
                  name="offline_method_reference"
                  placeholder="Bank / check reference number"
                />
              </Col>
            </Row>
          </Form>
        </div>
      )}
      {effectiveShowCardLayout && !showCardComingSoonMessage && (
        <p className="text-sm font-[Poppins] px-2 mb-2">{paymentHelperText}</p>
      )}
      {effectiveShowCardLayout && (
        <>
          {showCardComingSoonMessage ? (
            <div className="px-2">
              <div className="w-full rounded-md border border-[#91d5ff] bg-[#e6f7ff] px-4 py-3 text-center">
                <p className="text-sm font-[Poppins] text-[#003a8c] m-0 whitespace-pre-line">
                  {cardComingSoonMessage}
                </p>
              </div>
            </div>
          ) : (
            <div className="px-2">
              <PaymentForm
                applicationId={applicationId}
                locationId={locationId}
                createPaymentRequest={createPaymentRequest}
                cardTokenizeResponseReceived={handleTokenReceived}
              >
                {/* Digital wallets — logo-only buttons */}
                <div
                  id="make-payment-wallets"
                  className={`mb-1.5 grid gap-1.5 ${
                    applePayAvailable ? "grid-cols-2" : "grid-cols-1"
                  }`}
                >
                  <div className="relative w-full h-[30px] rounded border border-[#dadce0] bg-white overflow-hidden flex items-center justify-center">
                    <div className="sq-wallet-hit" aria-hidden>
                      <GooglePay />
                    </div>
                    <div className="pointer-events-none z-[1]">
                      <GooglePayLogo />
                    </div>
                  </div>
                  {applePayAvailable ? (
                    <div className="relative w-full h-[30px] rounded border border-[#dadce0] bg-white overflow-hidden flex items-center justify-center">
                      <div className="sq-wallet-hit" aria-hidden>
                        <ApplePay />
                      </div>
                      <div className="pointer-events-none z-[1]">
                        <ApplePayLogo />
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="flex items-center gap-2 my-1.5">
                  <div className="flex-1 h-px bg-[#e5e7eb]" />
                  <span className="text-[11px] text-[#64748b] font-[Poppins]">
                    or
                  </span>
                  <div className="flex-1 h-px bg-[#e5e7eb]" />
                </div>

                {cardsLoading ? (
                  <div className="flex items-center justify-center gap-2 py-8 text-[#64748b]">
                    <LoadingOutlined className="text-[16px]" />
                    <span className="text-sm font-[Poppins]">
                      Loading payment methods…
                    </span>
                  </div>
                ) : cardsError ? (
                  <p className="text-sm font-[Poppins] m-0 py-4 text-center text-red-500">
                    Could not load saved cards.
                  </p>
                ) : (
                  <>
                    {showCardTabs ? (
                      <div className="grid grid-cols-2 gap-1 p-0.5 mb-1.5 rounded-md bg-[#f1f5f9]">
                        <button
                          type="button"
                          onClick={() => setCardTab("saved")}
                          className={`inline-flex items-center justify-center gap-1.5 rounded h-[30px] text-[12px] font-[Poppins] leading-none border-0 cursor-pointer ${
                            activeCardTab === "saved"
                              ? "bg-white text-black shadow-sm"
                              : "bg-transparent text-[#64748b]"
                          }`}
                        >
                          <CreditCardOutlined className="text-[12px] leading-none flex items-center" />
                          <span className="leading-none">Saved card</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setCardTab("new")}
                          className={`inline-flex items-center justify-center gap-1.5 rounded h-[30px] text-[12px] font-[Poppins] leading-none border-0 cursor-pointer ${
                            activeCardTab === "new"
                              ? "bg-white text-black shadow-sm"
                              : "bg-transparent text-[#64748b]"
                          }`}
                        >
                          <PlusOutlined className="text-[12px] leading-none flex items-center" />
                          <span className="leading-none">New card</span>
                        </button>
                      </div>
                    ) : null}

                    {activeCardTab === "saved" && showCardTabs ? (
                      <div className="flex flex-col gap-2.5 mb-2 max-h-[min(320px,48vh)] sm:max-h-none overflow-y-auto sm:overflow-visible">
                        {cards.map((card) => {
                          const selected =
                            String(card.DealerCardNo) ===
                            String(selectedCardId);
                          const titleParts = [
                            card.CardBrand || null,
                            card.CardLast4 ? `•••• ${card.CardLast4}` : null,
                          ].filter(Boolean);
                          const title =
                            titleParts.length > 0
                              ? titleParts.join(" ")
                              : "Saved card";
                          const exp = formatExpiresOn(card);
                          return (
                            <button
                              key={String(card.DealerCardNo)}
                              type="button"
                              onClick={() =>
                                setSelectedCardId(card.DealerCardNo)
                              }
                              className={`w-full text-left rounded-md border px-3 py-2.5 flex items-center gap-3 cursor-pointer transition-colors ${
                                selected
                                  ? "border-[var(--text-color,#1677ff)] bg-[var(--text-color-op-10,#e6f4ff)]"
                                  : "border-[#e5e7eb] bg-white hover:border-[#cbd5e1]"
                              }`}
                            >
                              <CardBrandIcon
                                brandKey={brandKeyFromCard(card)}
                                className="shrink-0"
                              />
                              <div className="min-w-0 flex-1">
                                <p className="m-0 text-[13px] font-medium font-[Poppins] text-black leading-snug">
                                  {title}
                                </p>
                                <p className="m-0 mt-0.5 text-[11px] text-[#64748b] font-[Poppins] leading-snug">
                                  {[card.CardHolderName || null, exp || null]
                                    .filter(Boolean)
                                    .join(" · ") || "Saved payment method"}
                                </p>
                              </div>
                              <span
                                className={`shrink-0 h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                                  selected
                                    ? "border-[var(--text-color,#1677ff)]"
                                    : "border-[#cbd5e1]"
                                }`}
                                aria-hidden
                              >
                                {selected ? (
                                  <span className="h-2 w-2 rounded-full bg-[var(--text-color,#1677ff)]" />
                                ) : null}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="mb-1">
                        <div
                          id="make-payment-square-root"
                          className="relative rounded border border-[#d9d9d9] px-2 py-1.5 bg-white min-h-[44px]"
                        >
                          <CreditCard
                            render={(ButtonEl) => (
                              <ButtonEl
                                aria-hidden
                                css={{
                                  position: "absolute",
                                  left: "-9999px",
                                  width: "1px",
                                  height: "1px",
                                  opacity: 0,
                                  overflow: "hidden",
                                }}
                              >
                                {""}
                              </ButtonEl>
                            )}
                          />
                        </div>
                        {canSaveNewCard ? (
                          <div className="flex items-center justify-between gap-3 mt-2.5 px-0.5">
                            <span className="text-[12px] font-[Poppins] text-black">
                              Save card for future use
                            </span>
                            <Switch
                              size="small"
                              checked={saveCardForFuture}
                              onChange={setSaveCardForFuture}
                            />
                          </div>
                        ) : null}
                      </div>
                    )}
                  </>
                )}
              </PaymentForm>

              <div className="flex items-center justify-center gap-1.5 text-gray-500 text-[12px] mt-3 mb-1">
                <LockOutlined className="text-[12px]" />
                <span>Your payment is securely processed</span>
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex justify-center space-x-2 mt-1.5">
        <Button
          type="primary"
          className="min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
          loading={loading}
          disabled={
            cardsLoading ||
            (effectiveShowCardLayout && showCardComingSoonMessage) ||
            (effectiveShowCardLayout &&
              !showCardComingSoonMessage &&
              activeCardTab === "saved" &&
              !selectedCard?.SquareCardID)
          }
          onClick={handlePayClick}
        >
          <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
          <span>Pay</span>
        </Button>
        <Button
          className="reset-button"
          onClick={() => setPaymentModal(false)}
          disabled={loading}
        >
          <CloseOutlined />
          <span>Cancel</span>
        </Button>
      </div>
    </>
  );
};

export default MyPaymentForm;
